import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Award,
  CalendarDays,
  Check,
  CheckCircle2,
  CreditCard,
  FileSignature,
  FileText,
  Globe,
  Layers3,
  Loader2,
  Mail,
  MapPin,
  Phone,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import type { DigitalProposal, ManufacturerColor, TierKey } from "@/types/digitalProposal";
import type { DigitalProposalTemplate } from "@/types/digitalProposalTemplate";

const fmtCurrency = (n: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n || 0);

const fmtShortDate = (dateLike?: string | null) => {
  if (!dateLike) return "Today";
  return new Date(dateLike).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
  });
};

const fmtLongDate = (dateLike?: string | null) => {
  if (!dateLike) return "Issued today";
  return new Date(dateLike).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

const calculateMonthlyPayment = (amount: number, apr = 0.0599, months = 60) => {
  if (!amount) return 0;
  const monthlyRate = apr / 12;
  return (amount * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -months));
};

const fallbackWorkItems = [
  "Complete tear-off and removal of the existing roofing system down to the deck.",
  "Inspect decking and replace compromised sections as needed before installation.",
  "Install underlayment, starter, flashing, ventilation, and all code-required accessories.",
  "Install the selected roofing system in the homeowner's chosen color and finish package.",
  "Perform full cleanup, magnetic nail sweep, and final walkthrough at completion.",
];

function deriveWorkItems(scopeSummary?: string | null, tierName?: string) {
  const parsed = (scopeSummary || "")
    .split(/\n|•|\.|;/)
    .map((item) => item.replace(/^[-\d\s)]+/, "").trim())
    .filter(Boolean);

  if (parsed.length >= 4) return parsed.slice(0, 10);

  return fallbackWorkItems.map((item, index) =>
    index === 3 && tierName ? `Install the ${tierName} package in the homeowner's selected color.` : item,
  );
}

function buildIntroLetter(proposal: DigitalProposal, template: DigitalProposalTemplate | null) {
  const companyName = template?.company_name || "Our team";
  const customerName = proposal.customer_name || "Homeowner";
  const address = proposal.property_address || "your property";
  const story =
    template?.about_body?.trim() ||
    `${companyName} is committed to craftsmanship, clear communication, and a smooth homeowner experience from first inspection through final cleanup.`;

  return [
    `Dear ${customerName},`,
    `Thank you for giving ${companyName} the opportunity to provide this proposal for ${address}. We know replacing your roof is a major decision, and this page is designed to make that decision clear and easy to review.`,
    story,
    `We've outlined your recommended system options, product information, color choices, financing path, and signature step below so you can move forward with confidence.`,
  ];
}

export default function PublicDigitalProposal() {
  const { shareId } = useParams();
  const [proposal, setProposal] = useState<DigitalProposal | null>(null);
  const [template, setTemplate] = useState<DigitalProposalTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [tier, setTier] = useState<TierKey>("better");
  const [color, setColor] = useState<ManufacturerColor | null>(null);
  const [signOpen, setSignOpen] = useState(false);

  useEffect(() => {
    if (!shareId) return;

    // ---- Demo / preview mode (used by template editor "Preview" button) ----
    if (shareId === "preview") {
      const demoProposal = {
        id: "preview",
        org_id: "preview",
        share_id: "preview",
        title: "Sample Roof Replacement Proposal",
        status: "viewed",
        created_at: new Date().toISOString(),
        sent_at: new Date().toISOString(),
        first_viewed_at: new Date().toISOString(),
        scope_summary:
          "Complete tear-off and replacement of existing roof system.\nInstall new underlayment, starter, ice & water shield in valleys and eaves.\nReplace all flashing, pipe boots, and ridge vent.\nFull cleanup and magnetic nail sweep.",
        cover_photo_url: "",
        terms: "Proposal valid for 30 days. 50% deposit required to schedule.",
        financing_enabled: true,
        deposit_enabled: true,
        selected_tier: "better",
        selected_color: null,
        customer_name: "Sample Homeowner",
        customer_email: "homeowner@example.com",
        property_address: "123 Main Street, Anytown, USA",
        tiers: [
          {
            key: "good",
            name: "Good",
            price: 12500,
            highlight: false,
            included: ["Architectural Shingles", "Standard Underlayment", "10-Year Workmanship"],
            colors: [{ name: "Weathered Wood", hex: "#6b5e4f" }],
          },
          {
            key: "better",
            name: "Better",
            price: 16800,
            highlight: true,
            included: [
              "Premium Architectural Shingles",
              "Synthetic Underlayment",
              "Ice & Water Shield",
              "Ridge Vent System",
              "15-Year Workmanship",
            ],
            colors: [
              { name: "Pewter Gray", hex: "#7a7a7a" },
              { name: "Onyx Black", hex: "#1a1a1a" },
            ],
          },
          {
            key: "best",
            name: "Best",
            price: 22400,
            highlight: false,
            included: [
              "Designer Shingles (Lifetime)",
              "Synthetic Underlayment",
              "Full Ice & Water Shield",
              "Ridge Vent + Soffit Vent",
              "Lifetime Workmanship",
              "Transferable Warranty",
            ],
            colors: [
              { name: "Charcoal", hex: "#36454f" },
              { name: "Driftwood", hex: "#8b7355" },
            ],
          },
        ],
      } as unknown as DigitalProposal;

      setProposal(demoProposal);
      setTier("better");
      setColor(demoProposal.tiers.find((t) => t.key === "better")?.colors?.[0] || null);
      setLoading(false);
      return;
    }

    (async () => {
      const { data, error } = await supabase
        .from("digital_proposals")
        .select("*")
        .eq("share_id", shareId)
        .maybeSingle();

      if (error || !data) {
        setLoading(false);
        return;
      }

      const loadedProposal = data as unknown as DigitalProposal;
      setProposal(loadedProposal);

      const initialTier =
        (loadedProposal.selected_tier as TierKey) ||
        (loadedProposal.tiers.find((item) => item.highlight)?.key as TierKey) ||
        loadedProposal.tiers[0]?.key ||
        "better";
      const initialColor =
        loadedProposal.selected_color ||
        loadedProposal.tiers.find((item) => item.key === initialTier)?.colors?.[0] ||
        null;

      setTier(initialTier);
      setColor(initialColor);

      const { data: tpl } = await supabase
        .from("digital_proposal_templates" as never)
        .select("*")
        .eq("org_id", loadedProposal.org_id)
        .maybeSingle();

      if (tpl) setTemplate(tpl as unknown as DigitalProposalTemplate);
      setLoading(false);

      if (!loadedProposal.first_viewed_at) {
        await supabase
          .from("digital_proposals")
          .update({
            first_viewed_at: new Date().toISOString(),
            status: loadedProposal.status === "draft" ? "viewed" : loadedProposal.status,
          })
          .eq("share_id", shareId);
      }
    })();
  }, [shareId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!proposal) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6">
        <Card className="p-12 text-center rounded-lg max-w-lg w-full">
          <p className="text-lg font-semibold">Proposal not found</p>
          <p className="text-sm text-muted-foreground mt-2">
            The link may be expired, incorrect, or no longer available.
          </p>
        </Card>
      </div>
    );
  }

  const activeTier = proposal.tiers.find((item) => item.key === tier) || proposal.tiers[0];
  const isSigned = proposal.status === "signed";
  const heroImage = proposal.cover_photo_url || template?.hero_photo_url || "";
  const issueDate = proposal.sent_at || proposal.created_at;
  const packageRange = proposal.tiers.length
    ? `${fmtCurrency(Math.min(...proposal.tiers.map((item) => item.price)))} – ${fmtCurrency(
        Math.max(...proposal.tiers.map((item) => item.price)),
      )}`
    : fmtCurrency(activeTier?.price || 0);
  const monthlyPayment = calculateMonthlyPayment(activeTier?.price || 0);
  const introLetter = buildIntroLetter(proposal, template);
  const workItems = deriveWorkItems(proposal.scope_summary, activeTier?.name);
  const systemComponents = activeTier?.included?.length
    ? activeTier.included.slice(0, 6)
    : [
        `${activeTier?.product_line || activeTier?.name} roofing system`,
        "Underlayment and starter accessories",
        "Flashing and ventilation components",
        "Manufacturer-aligned installation methods",
      ];
  const brochuresForTier = (template?.brochures || []).filter(
    (item) =>
      item.tier === tier &&
      (!activeTier?.manufacturer || item.manufacturer.toLowerCase() === activeTier.manufacturer.toLowerCase()),
  );
  const brochuresToShow = brochuresForTier.length
    ? brochuresForTier
    : (template?.brochures || []).filter((item) => item.tier === tier).slice(0, 3);
  const depositPercent = template?.deposit_required ? template.deposit_percent || 50 : 0;
  const financingUrl =
    template?.financing_apply_url || template?.financing_lenders?.[0]?.apply_url || "https://www.gethearth.com/";
  const companyName = template?.company_name || "RoofGenius";
  const primaryTierDescription =
    template?.hero_subheadline?.trim() ||
    `${activeTier?.manufacturer || companyName} ${activeTier?.product_line || activeTier?.name} system — professional installation with manufacturer-backed materials and a clean, review-ready homeowner experience.`;

  // Forced-light, trustworthy palette — overrides app theme tokens for the homeowner view.
  // Brand color defaults to a calm trust blue; can be overridden via template later.
  const forcedLightVars: React.CSSProperties = {
    ["--background" as any]: "0 0% 100%",
    ["--foreground" as any]: "222 47% 11%",
    ["--card" as any]: "0 0% 100%",
    ["--card-foreground" as any]: "222 47% 11%",
    ["--popover" as any]: "0 0% 100%",
    ["--popover-foreground" as any]: "222 47% 11%",
    ["--primary" as any]: "215 90% 42%",
    ["--primary-foreground" as any]: "0 0% 100%",
    ["--secondary" as any]: "210 40% 96%",
    ["--secondary-foreground" as any]: "222 47% 11%",
    ["--muted" as any]: "210 40% 96%",
    ["--muted-foreground" as any]: "215 16% 42%",
    ["--accent" as any]: "210 40% 96%",
    ["--accent-foreground" as any]: "222 47% 11%",
    ["--border" as any]: "214 32% 91%",
    ["--input" as any]: "214 32% 91%",
    ["--ring" as any]: "215 90% 42%",
  };

  return (
    <div style={forcedLightVars} className="min-h-screen bg-white text-slate-900">
      {/* Full-bleed hero with cover photo */}
      <section className="relative isolate overflow-hidden">
        <div className="absolute inset-0">
          {heroImage ? (
            <img src={heroImage} alt={proposal.title} className="h-full w-full object-cover" />
          ) : (
            <div className="h-full w-full bg-gradient-to-br from-slate-800 via-slate-900 to-black" />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/55 to-black/90" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/45 via-transparent to-transparent" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,hsl(215_90%_55%/0.28),transparent_55%)]" />
        </div>

        <div className="relative mx-auto flex min-h-[68svh] w-full max-w-7xl flex-col justify-between px-5 py-6 md:min-h-[80vh] md:px-10 lg:px-14 lg:py-10">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3.5 py-2 backdrop-blur-md">
              <Sparkles className="h-3.5 w-3.5 text-white" />
              <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/90 md:text-[11px]">
                Roof Proposal
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="border border-white/20 bg-white/10 text-white hover:bg-white/10 backdrop-blur-md">
                {isSigned ? "Accepted" : "Awaiting signature"}
              </Badge>
              {!isSigned && (
                <button
                  onClick={() => setSignOpen(true)}
                  className="inline-flex items-center gap-2 rounded-full bg-[hsl(215_90%_50%)] px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.18em] text-white shadow-lg shadow-blue-500/30 hover:bg-[hsl(215_90%_45%)] transition md:px-6 md:py-3 md:text-xs"
                >
                  <FileSignature className="h-3.5 w-3.5" />
                  Review &amp; Sign
                </button>
              )}
            </div>
          </div>

          <div className="mt-8 grid items-end gap-6 pb-6 md:pb-10 lg:grid-cols-[1.4fr_0.85fr] lg:gap-16">
            <div className="space-y-6">
              {(() => {
                const logo = (template as any)?.company_logo_url as string | undefined;
                return logo ? (
                  <img
                    src={logo}
                    alt={companyName}
                    className="h-10 max-w-[200px] object-contain drop-shadow-lg brightness-0 invert md:h-12"
                  />
                ) : (
                  <p className="text-xs font-bold uppercase tracking-[0.38em] text-white/90">{companyName}</p>
                );
              })()}

              <div className="space-y-3">
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-white/85 md:text-sm">
                  Prepared for {proposal.customer_name || "Homeowner"}
                </p>
                <h1
                  className="text-4xl font-black leading-[0.95] tracking-tight text-white sm:text-5xl md:text-6xl lg:text-7xl"
                  style={{ textShadow: "0 2px 24px rgba(0,0,0,0.5)" }}
                >
                  Your New Roof<br className="hidden sm:block" /> Replacement Proposal
                </h1>
                <p className="max-w-xl text-sm leading-relaxed text-white/85 md:text-base md:leading-loose">
                  {primaryTierDescription}
                </p>
              </div>

              <div className="grid grid-cols-3 gap-2.5 sm:gap-4 max-w-2xl">
                <div className="rounded-2xl border border-white/25 bg-white/15 p-3.5 backdrop-blur-sm sm:rounded-3xl sm:p-5">
                  <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-white/75 sm:text-[10px]">Investment</p>
                  <p className="mt-1.5 text-base font-black text-white sm:mt-2 sm:text-xl md:text-2xl">{packageRange}</p>
                  <p className="mt-0.5 text-[10px] text-white/75 sm:mt-1 sm:text-xs">Choose your package</p>
                </div>
                <div className="rounded-2xl border border-white/25 bg-white/15 p-3.5 backdrop-blur-sm sm:rounded-3xl sm:p-5">
                  <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-white/75 sm:text-[10px]">System</p>
                  <p className="mt-1.5 text-sm font-black text-white sm:mt-2 sm:text-base md:text-lg leading-tight">
                    {activeTier?.product_line || activeTier?.name || "Roof system"}
                  </p>
                  <p className="mt-0.5 text-[10px] text-white/75 sm:mt-1 sm:text-xs truncate">
                    {activeTier?.warranty_years ? `${activeTier.warranty_years}-yr warranty` : "Manufacturer-backed"}
                  </p>
                </div>
                <div className="rounded-2xl border border-white/25 bg-white/15 p-3.5 backdrop-blur-sm sm:rounded-3xl sm:p-5">
                  <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-white/75 sm:text-[10px]">Date</p>
                  <p className="mt-1.5 text-sm font-black text-white sm:mt-2 sm:text-base">
                    {fmtShortDate(issueDate)}
                  </p>
                  <p className="mt-0.5 text-[10px] text-white/75 sm:mt-1 sm:text-xs">
                    {new Date(issueDate).getFullYear()}
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap gap-3 pt-2">
                <a
                  href="#packages"
                  className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-xs font-bold uppercase tracking-[0.18em] text-slate-900 shadow-lg hover:bg-slate-100 transition"
                >
                  Choose your package
                </a>
                {proposal.financing_enabled && (
                  <button
                    onClick={() => window.open(financingUrl, "_blank", "noopener,noreferrer")}
                    className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-6 py-3 text-xs font-bold uppercase tracking-[0.18em] text-white backdrop-blur-md hover:bg-white/20 transition"
                  >
                    <CreditCard className="h-4 w-4" />
                    Apply for financing
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-x-10 gap-y-3 px-6 py-5 text-xs font-semibold text-slate-600">
          <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[hsl(215_90%_42%)]" /> Manufacturer-backed warranty</div>
          <div className="flex items-center gap-2"><Award className="h-4 w-4 text-[hsl(215_90%_42%)]" /> Licensed &amp; insured</div>
          <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-[hsl(215_90%_42%)]" /> Secure digital signing</div>
          <div className="flex items-center gap-2"><Globe className="h-4 w-4 text-[hsl(215_90%_42%)]" /> Live online proposal</div>
        </div>
      </div>

      {/* Old duplicate hero removed — premium hero above replaces it */}
      <section className="border-b border-border bg-muted/30">
        <div className="mx-auto grid max-w-7xl gap-8 px-4 py-10 sm:px-6 lg:grid-cols-[1.2fr_0.8fr] lg:px-8 lg:py-14">
          <div className="rounded-lg border border-border bg-card p-6 shadow-sm sm:p-8">
            <div className="mb-5 flex items-center justify-between gap-3">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary">Introduction</p>
                <h3 className="mt-2 text-2xl font-semibold">A proposal built to be easy to review</h3>
              </div>
              <Badge className="border border-border bg-muted text-foreground hover:bg-muted">
                {proposal.status === "viewed" ? "Viewed" : proposal.status}
              </Badge>
            </div>
            <div className="space-y-4 text-base leading-7 text-muted-foreground">
              {introLetter.map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>
            <div className="mt-6 border-t border-border pt-5">
              <p className="font-semibold text-foreground">{companyName}</p>
              <p className="text-sm text-muted-foreground">{template?.about_headline || "Professional roof replacement partner"}</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="rounded-lg border border-border bg-card p-5 shadow-sm">
              <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary">Why homeowners choose us</p>
              <div className="mt-4 space-y-4">
                <TrustRow
                  icon={<ShieldCheck className="h-4 w-4" />}
                  title="Clear scope"
                  body="Every major system component, scope item, and next step is organized in one proposal link."
                />
                <TrustRow
                  icon={<Layers3 className="h-4 w-4" />}
                  title="Good / Better / Best"
                  body="Package comparison is built for fast decision-making instead of a confusing quote PDF."
                />
                <TrustRow
                  icon={<CreditCard className="h-4 w-4" />}
                  title="Financing built in"
                  body="Homeowners can review pricing, estimate payments, and move to an application without leaving the proposal."
                />
              </div>
            </div>

            {(template?.years_in_business || template?.license_number || template?.certifications?.length) && (
              <div className="rounded-lg border border-border bg-card p-5 shadow-sm">
                <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary">Company profile</p>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  {template?.years_in_business ? (
                    <div>
                      <p className="text-3xl font-semibold text-foreground">{template.years_in_business}+</p>
                      <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Years in business</p>
                    </div>
                  ) : null}
                  {template?.license_number ? (
                    <div>
                      <p className="text-lg font-semibold text-foreground">Licensed &amp; Insured</p>
                      <p className="text-sm text-muted-foreground">License #{template.license_number}</p>
                    </div>
                  ) : null}
                </div>
                {template?.certifications?.length ? (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {template.certifications.map((certification, index) => (
                      <div
                        key={`${certification.name}-${index}`}
                        className="inline-flex items-center gap-2 rounded-md border border-border bg-muted px-3 py-2 text-sm"
                      >
                        {certification.logo_url ? (
                          <img src={certification.logo_url} alt={certification.name} className="h-5 w-auto object-contain" />
                        ) : (
                          <Award className="h-4 w-4 text-primary" />
                        )}
                        <span>{certification.name}</span>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="border-b border-border bg-background">
        <div className="mx-auto grid max-w-7xl gap-8 px-4 py-10 sm:px-6 lg:grid-cols-[0.95fr_1.05fr] lg:px-8 lg:py-14">
          <div className="space-y-5">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary">Scope of work</p>
              <h3 className="mt-2 text-3xl font-semibold">Complete Scope of Work</h3>
              <p className="mt-3 max-w-2xl text-base leading-7 text-muted-foreground">
                {proposal.scope_summary || "Every major layer, accessory, and finish detail is outlined below for a clean install and confident approval."}
              </p>
            </div>

            <div className="overflow-hidden rounded-lg border border-border bg-card shadow-sm">
              <AspectRatio ratio={16 / 10}>
                {heroImage ? (
                  <img src={heroImage} alt={activeTier?.product_line || proposal.title} className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center bg-muted text-muted-foreground">
                    System preview unavailable
                  </div>
                )}
              </AspectRatio>
              <div className="p-5">
                <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary">Installed system</p>
                <h4 className="mt-2 text-xl font-semibold">
                  {activeTier?.manufacturer ? `${activeTier.manufacturer} ${activeTier.product_line || activeTier.name}` : activeTier?.name}
                </h4>
                <p className="mt-2 text-sm leading-6 text-muted-foreground">
                  Designed for dependable protection, better curb appeal, and a cleaner homeowner handoff from selection through installation.
                </p>
                <div className="mt-5 grid gap-3 sm:grid-cols-2">
                  {systemComponents.map((component, index) => (
                    <div key={`${component}-${index}`} className="rounded-md border border-border bg-muted/40 p-3">
                      <div className="text-xs font-semibold text-primary">{String(index + 1).padStart(2, "0")}</div>
                      <div className="mt-1 text-sm font-medium text-foreground">{component}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-6 shadow-sm sm:p-8">
            <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary">Work to be performed</p>
            <div className="mt-5 grid gap-4">
              {workItems.map((item, index) => (
                <div key={`${item}-${index}`} className="grid grid-cols-[40px_1fr] gap-4 rounded-md border border-border bg-muted/30 p-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10 text-sm font-semibold text-primary">
                    {String(index + 1).padStart(2, "0")}
                  </div>
                  <p className="text-sm leading-6 text-foreground">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="packages" className="scroll-mt-24 border-b border-border bg-muted/20">
        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8 lg:py-14">
          <div className="max-w-2xl">
            <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary">Choose your package</p>
            <h3 className="mt-2 text-3xl font-semibold">Pick Your Package</h3>
            <p className="mt-3 text-base leading-7 text-muted-foreground">
              Select the package that fits your budget and performance goals. The homeowner-facing experience stays clean while still giving real decision-making detail.
            </p>
          </div>

          <div className="mt-8 grid gap-4 lg:grid-cols-3">
            {proposal.tiers.map((item) => {
              const selected = item.key === tier;
              const monthly = calculateMonthlyPayment(item.price);
              const label = item.key === "good" ? "Best value" : item.key === "better" ? "Most popular" : "Top tier";

              return (
                <button
                  key={item.key}
                  type="button"
                  disabled={isSigned}
                  onClick={() => {
                    setTier(item.key);
                    setColor(item.colors?.[0] || null);
                  }}
                  className={`group rounded-lg border p-5 text-left transition-all ${
                    selected
                      ? "border-primary bg-card shadow-[0_14px_36px_-20px_hsl(var(--primary)/0.55)]"
                      : "border-border bg-card shadow-sm hover:border-primary/40"
                  } ${isSigned ? "cursor-default" : "cursor-pointer"}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <Badge
                        className={`border ${
                          selected
                            ? "border-primary/20 bg-primary/10 text-primary hover:bg-primary/10"
                            : "border-border bg-muted text-foreground hover:bg-muted"
                        }`}
                      >
                        {label}
                      </Badge>
                      <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">{item.key}</p>
                    </div>
                    {selected && <CheckCircle2 className="h-5 w-5 text-primary" />}
                  </div>

                  <div className="mt-4 space-y-2">
                    <div className="text-3xl font-semibold text-foreground">{fmtCurrency(item.price)}</div>
                    {proposal.financing_enabled && (
                      <p className="text-sm text-muted-foreground">As low as {fmtCurrency(monthly)}/mo</p>
                    )}
                    <h4 className="text-xl font-semibold text-foreground">
                      {item.manufacturer ? `${item.manufacturer} ${item.product_line || item.name}` : item.name}
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      {item.key === "good"
                        ? "Reliable protection with a clean, proven system."
                        : item.key === "better"
                          ? "The balance most homeowners choose."
                          : "Premium curb appeal and elevated warranty story."}
                    </p>
                  </div>

                  <div className="mt-5 space-y-2">
                    {(item.included || []).slice(0, 5).map((feature, index) => (
                      <div key={`${feature}-${index}`} className="flex items-start gap-2 text-sm text-foreground">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      <section className="border-b border-border bg-background">
        <div className="mx-auto grid max-w-7xl gap-8 px-4 py-10 sm:px-6 lg:grid-cols-[1fr_0.95fr] lg:px-8 lg:py-14">
          <div className="space-y-5">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary">System brochure + color board</p>
              <h3 className="mt-2 text-3xl font-semibold">Your Roof System &amp; Colors</h3>
              <p className="mt-3 text-base leading-7 text-muted-foreground">
                Browse product information, review package details, and confirm the color direction for your selected system.
              </p>
            </div>

            <div className="rounded-lg border border-border bg-card p-6 shadow-sm">
              <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary">Product summary</p>
              <h4 className="mt-2 text-2xl font-semibold">{activeTier?.product_line || activeTier?.name}</h4>
              <p className="mt-3 text-sm leading-6 text-muted-foreground">
                {activeTier?.manufacturer || companyName} shingles and accessories are positioned here as a complete system story instead of a plain line-item quote, so the homeowner understands what they're buying.
              </p>

              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                <div className="rounded-md border border-border bg-muted/40 p-4">
                  <p className="text-sm font-semibold text-foreground">Warranty + performance</p>
                  <div className="mt-3 space-y-2 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-primary" />{activeTier?.warranty_years ? `${activeTier.warranty_years}-year coverage` : "Manufacturer-backed coverage"}</div>
                    <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-primary" />Wind and accessory performance support</div>
                    <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-primary" />System-based installation approach</div>
                  </div>
                </div>
                <div className="rounded-md border border-border bg-muted/40 p-4">
                  <p className="text-sm font-semibold text-foreground">Brochures</p>
                  <div className="mt-3 space-y-3">
                    {brochuresToShow.length > 0 ? (
                      brochuresToShow.map((brochure) => (
                        <a
                          key={brochure.id}
                          href={brochure.brochure_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 rounded-md border border-border bg-card px-3 py-3 text-sm transition-colors hover:border-primary/40"
                        >
                          {brochure.thumbnail_url ? (
                            <img src={brochure.thumbnail_url} alt={brochure.product_line} className="h-10 w-10 rounded object-cover" />
                          ) : (
                            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10 text-primary">
                              <FileText className="h-5 w-5" />
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <p className="truncate font-medium text-foreground">{brochure.product_line}</p>
                            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{brochure.manufacturer}</p>
                          </div>
                        </a>
                      ))
                    ) : (
                      <p className="text-sm text-muted-foreground">Brochures can be loaded per manufacturer and tier here.</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-6 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary">Choose your color</p>
                <h4 className="mt-2 text-2xl font-semibold">Shingle Color Board</h4>
              </div>
              {color && <Badge className="border border-primary/20 bg-primary/10 text-primary hover:bg-primary/10">{color.name}</Badge>}
            </div>
            <p className="mt-3 text-sm leading-6 text-muted-foreground">
              {isSigned ? "Color is locked in with the signed proposal." : "Your selection updates the proposal and will be saved when the homeowner signs."}
            </p>

            {activeTier?.colors?.length ? (
              <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
                {activeTier.colors.map((swatch) => {
                  const selected = color?.hex === swatch.hex;
                  return (
                    <button
                      key={swatch.hex}
                      type="button"
                      disabled={isSigned}
                      onClick={() => setColor(swatch)}
                      className={`rounded-lg border p-3 text-left transition-all ${
                        selected
                          ? "border-primary bg-primary/5 shadow-sm"
                          : "border-border bg-background hover:border-primary/40"
                      } ${isSigned ? "cursor-default" : "cursor-pointer"}`}
                    >
                      <div className="overflow-hidden rounded-md border border-border/70 bg-muted">
                        {swatch.image_url ? (
                          <AspectRatio ratio={4 / 3}>
                            <img src={swatch.image_url} alt={swatch.name} className="h-full w-full object-cover" />
                          </AspectRatio>
                        ) : (
                          <AspectRatio ratio={4 / 3}>
                            <div className="h-full w-full" style={{ backgroundColor: swatch.hex }} />
                          </AspectRatio>
                        )}
                      </div>
                      <div className="mt-3 flex items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-medium text-foreground">{swatch.name}</p>
                          <p className="text-xs text-muted-foreground">{selected ? "Selected" : "Tap to choose"}</p>
                        </div>
                        {selected && <CheckCircle2 className="h-4 w-4 text-primary" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="mt-6 rounded-lg border border-dashed border-border bg-muted/40 p-6 text-sm text-muted-foreground">
                No color board has been added to this package yet.
              </div>
            )}
          </div>
        </div>
      </section>

      <section id="sign" className="scroll-mt-24 border-b border-border bg-muted/25">
        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8 lg:py-14">
          <div className="grid gap-6 lg:grid-cols-[1.05fr_0.95fr]">
            <div className="rounded-lg border border-primary/20 bg-card p-6 shadow-[0_18px_40px_-28px_hsl(var(--primary)/0.55)] sm:p-8">
              <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary">Accept &amp; sign</p>
              <h3 className="mt-2 text-3xl font-semibold">Ready to Move Forward?</h3>
              <p className="mt-3 text-base leading-7 text-muted-foreground">
                Review your selected package, confirm your color direction, and sign to lock in your project.
              </p>

              <div className="mt-6 grid gap-4 sm:grid-cols-3">
                <SummaryMetric label="Project pricing" value={fmtCurrency(activeTier?.price || 0)} />
                {proposal.financing_enabled ? (
                  <SummaryMetric label="Estimated financing" value={`${fmtCurrency(monthlyPayment)}/mo`} caption="At 5.99% APR · 60 months" />
                ) : (
                  <SummaryMetric label="Selection" value={activeTier?.key?.toUpperCase() || "—"} />
                )}
                <SummaryMetric
                  label="Payment terms"
                  value={depositPercent ? `${depositPercent}% / ${100 - depositPercent}%` : "Due on completion"}
                  caption={depositPercent ? "Deposit on signing / balance at completion" : undefined}
                />
              </div>

              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                <div className="rounded-lg border border-border bg-muted/30 p-4">
                  <p className="text-sm font-semibold text-foreground">Your selections</p>
                  <div className="mt-3 space-y-2 text-sm text-muted-foreground">
                    <div className="flex items-center justify-between gap-4">
                      <span>Package</span>
                      <span className="font-medium text-foreground">{activeTier?.name}</span>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <span>Color</span>
                      <span className="font-medium text-foreground">{color?.name || "To be selected"}</span>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <span>Prepared for</span>
                      <span className="font-medium text-foreground">{proposal.customer_name || "Homeowner"}</span>
                    </div>
                  </div>
                </div>
                <div className="rounded-lg border border-border bg-muted/30 p-4">
                  <p className="text-sm font-semibold text-foreground">Questions? Reach us</p>
                  <div className="mt-3 space-y-2 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" />{companyName}</div>
                    {proposal.customer_phone ? <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-primary" />{proposal.customer_phone}</div> : null}
                    {proposal.customer_email ? <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-primary" />{proposal.customer_email}</div> : null}
                    <div className="flex items-center gap-2"><Globe className="h-4 w-4 text-primary" />Digital proposal link</div>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                {isSigned ? (
                  <div className="inline-flex items-center gap-2 rounded-lg border border-success/25 bg-success/10 px-4 py-3 text-sm font-medium text-foreground">
                    <CheckCircle2 className="h-5 w-5 text-success" />
                    Signed by {proposal.signed_by_name}
                  </div>
                ) : (
                  <>
                    <Button size="lg" onClick={() => setSignOpen(true)} className="gap-2">
                      <FileSignature className="h-5 w-5" />
                      Accept &amp; sign
                    </Button>
                    {proposal.financing_enabled && (
                      <Button
                        size="lg"
                        variant="outline"
                        className="gap-2"
                        onClick={() => window.open(financingUrl, "_blank", "noopener,noreferrer")}
                      >
                        <CreditCard className="h-5 w-5" />
                        Apply for financing
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="space-y-4">
              <div className="rounded-lg border border-border bg-card p-5 shadow-sm">
                <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary">Proposal timeline</p>
                <div className="mt-4 space-y-4">
                  <TimelineItem title="Proposal created" value={fmtLongDate(proposal.created_at)} />
                  <TimelineItem title="First viewed" value={proposal.first_viewed_at ? fmtLongDate(proposal.first_viewed_at) : "Pending homeowner view"} />
                  <TimelineItem title="Signature status" value={isSigned ? `Signed ${fmtLongDate(proposal.signed_at)}` : "Awaiting signature"} />
                </div>
              </div>

              {(template?.financing_lenders?.length || proposal.terms) && (
                <div className="rounded-lg border border-border bg-card p-5 shadow-sm">
                  {template?.financing_lenders?.length ? (
                    <>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary">Financing partners</p>
                      <div className="mt-4 flex flex-wrap gap-2">
                        {template.financing_lenders.map((lender, index) => (
                          <a
                            key={`${lender.name}-${index}`}
                            href={lender.apply_url || financingUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 rounded-md border border-border bg-muted px-3 py-2 text-sm transition-colors hover:border-primary/40"
                          >
                            {lender.logo_url ? (
                              <img src={lender.logo_url} alt={lender.name} className="h-5 w-auto object-contain" />
                            ) : (
                              <CreditCard className="h-4 w-4 text-primary" />
                            )}
                            <span>{lender.name}</span>
                          </a>
                        ))}
                      </div>
                    </>
                  ) : null}

                  {proposal.terms ? (
                    <div className={template?.financing_lenders?.length ? "mt-6 border-t border-border pt-6" : ""}>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary">Terms &amp; conditions</p>
                      <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-muted-foreground">{proposal.terms}</p>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <SignAcceptDialog
        open={signOpen}
        onOpenChange={setSignOpen}
        proposal={proposal}
        tier={tier}
        color={color}
        onSigned={(updatedProposal) => {
          setProposal(updatedProposal);
          setSignOpen(false);
          toast.success("Thank you — your proposal has been signed.");
        }}
      />
    </div>
  );
}

function DetailStat({ label, value, secondary }: { label: string; value: string; secondary?: string }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 shadow-sm">
      <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">{label}</p>
      <div className="mt-2 text-xl font-semibold text-foreground">{value}</div>
      {secondary ? <div className="mt-1 text-xs uppercase tracking-[0.22em] text-muted-foreground">{secondary}</div> : null}
    </div>
  );
}

function InfoPanel({
  icon,
  label,
  title,
  lines,
}: {
  icon: React.ReactNode;
  label: string;
  title: string;
  lines: string[];
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 shadow-sm">
      <div className="flex items-center gap-2 text-primary">
        {icon}
        <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">{label}</p>
      </div>
      <p className="mt-3 text-lg font-semibold text-foreground">{title}</p>
      <div className="mt-2 space-y-1.5 text-sm text-muted-foreground">
        {lines.map((line) => (
          <p key={line}>{line}</p>
        ))}
      </div>
    </div>
  );
}

function TrustRow({ icon, title, body }: { icon: React.ReactNode; title: string; body: string }) {
  return (
    <div className="flex items-start gap-3 rounded-md border border-border bg-muted/30 p-4">
      <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">{icon}</div>
      <div>
        <p className="font-medium text-foreground">{title}</p>
        <p className="mt-1 text-sm leading-6 text-muted-foreground">{body}</p>
      </div>
    </div>
  );
}

function SummaryMetric({
  label,
  value,
  caption,
}: {
  label: string;
  value: string;
  caption?: string;
}) {
  return (
    <div className="rounded-lg border border-border bg-muted/25 p-4">
      <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">{label}</p>
      <div className="mt-2 text-2xl font-semibold text-foreground">{value}</div>
      {caption ? <p className="mt-1 text-xs text-muted-foreground">{caption}</p> : null}
    </div>
  );
}

function TimelineItem({ title, value }: { title: string; value: string }) {
  return (
    <div className="flex items-start gap-3 rounded-md border border-border bg-muted/30 p-4">
      <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
        <CalendarDays className="h-4 w-4" />
      </div>
      <div>
        <p className="font-medium text-foreground">{title}</p>
        <p className="mt-1 text-sm text-muted-foreground">{value}</p>
      </div>
    </div>
  );
}

function SignAcceptDialog({
  open,
  onOpenChange,
  proposal,
  tier,
  color,
  onSigned,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  proposal: DigitalProposal;
  tier: TierKey;
  color: ManufacturerColor | null;
  onSigned: (proposal: DigitalProposal) => void;
}) {
  const [name, setName] = useState(proposal.customer_name || "");
  const [email, setEmail] = useState(proposal.customer_email || "");
  const [saving, setSaving] = useState(false);
  const [signatureMode, setSignatureMode] = useState<"draw" | "type">("draw");
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const hasInk = useRef(false);

  useEffect(() => {
    if (!open) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext("2d");
    if (!context) return;

    context.fillStyle = "hsl(0 0% 100%)";
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.strokeStyle = "hsl(222 47% 11%)";
    context.lineWidth = 2;
    context.lineCap = "round";
    hasInk.current = false;
  }, [open]);

  const getPointerPosition = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((event.clientX - rect.left) / rect.width) * canvas.width,
      y: ((event.clientY - rect.top) / rect.height) * canvas.height,
    };
  };

  const buildTypedSignatureDataUrl = (fullName: string) => {
    const canvas = document.createElement("canvas");
    canvas.width = 1000;
    canvas.height = 260;
    const context = canvas.getContext("2d");
    if (!context) return null;

    context.fillStyle = "hsl(0 0% 100%)";
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.fillStyle = "hsl(222 47% 11%)";
    context.font = '72px "Brush Script MT", "Segoe Script", cursive';
    context.fillText(fullName, 48, 160);
    return canvas.toDataURL("image/png");
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext("2d");
    if (!context) return;

    context.fillStyle = "hsl(0 0% 100%)";
    context.fillRect(0, 0, canvas.width, canvas.height);
    hasInk.current = false;
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error("Please enter your name");
      return;
    }

    let signatureData: string | null = null;

    if (signatureMode === "draw") {
      if (!hasInk.current) {
        toast.error("Please sign in the box");
        return;
      }
      signatureData = canvasRef.current?.toDataURL("image/png") || null;
    } else {
      signatureData = buildTypedSignatureDataUrl(name.trim());
      if (!signatureData) {
        toast.error("Unable to create typed signature");
        return;
      }
    }

    setSaving(true);
    try {
      const { data, error } = await supabase
        .from("digital_proposals")
        .update({
          status: "signed",
          signed_at: new Date().toISOString(),
          signed_by_name: name.trim(),
          signed_by_email: email.trim() || null,
          signature_data: signatureData,
          selected_tier: tier,
          selected_color: color as never,
        })
        .eq("share_id", proposal.share_id)
        .select("*")
        .single();

      if (error) throw error;
      onSigned(data as unknown as DigitalProposal);
      // Fire-and-forget: generate signed PDF + email both parties
      supabase.functions
        .invoke("finalize-signed-proposal", { body: { share_id: proposal.share_id } })
        .catch((e) => console.error("finalize-signed-proposal failed", e));
    } catch (error: any) {
      toast.error(error.message || "Failed to save signature");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl rounded-lg border-border">
        <DialogHeader>
          <DialogTitle className="text-2xl">Accept This Proposal</DialogTitle>
          <DialogDescription className="text-sm leading-6 text-muted-foreground">
            By signing, you agree to the scope of work and pricing for the {proposal.tiers.find((item) => item.key === tier)?.name}
            {color ? ` package in ${color.name}` : " package"}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Full name</Label>
              <Input value={name} onChange={(event) => setName(event.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={email} onChange={(event) => setEmail(event.target.value)} />
            </div>
          </div>

          <div className="rounded-lg border border-border bg-muted/20 p-4">
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setSignatureMode("type")}
                className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  signatureMode === "type" ? "bg-primary text-primary-foreground" : "bg-card text-foreground border border-border"
                }`}
              >
                Type Signature
              </button>
              <button
                type="button"
                onClick={() => setSignatureMode("draw")}
                className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  signatureMode === "draw" ? "bg-primary text-primary-foreground" : "bg-card text-foreground border border-border"
                }`}
              >
                Draw Signature
              </button>
            </div>

            {signatureMode === "type" ? (
              <div className="mt-4 rounded-lg border border-dashed border-border bg-card p-6">
                <p className="text-sm text-muted-foreground">Typed signature preview</p>
                <div className="mt-4 min-h-28 rounded-md border border-border bg-background px-4 py-6 text-4xl text-foreground" style={{ fontFamily: '"Brush Script MT", "Segoe Script", cursive' }}>
                  {name || "Your signature"}
                </div>
              </div>
            ) : (
              <div className="mt-4">
                <canvas
                  ref={canvasRef}
                  width={720}
                  height={220}
                  className="h-48 w-full touch-none rounded-lg border-2 border-dashed border-border bg-card"
                  onPointerDown={(event) => {
                    drawing.current = true;
                    const context = canvasRef.current?.getContext("2d");
                    if (!context) return;
                    const point = getPointerPosition(event);
                    context.beginPath();
                    context.moveTo(point.x, point.y);
                  }}
                  onPointerMove={(event) => {
                    if (!drawing.current) return;
                    const context = canvasRef.current?.getContext("2d");
                    if (!context) return;
                    const point = getPointerPosition(event);
                    context.lineTo(point.x, point.y);
                    context.stroke();
                    hasInk.current = true;
                  }}
                  onPointerUp={() => {
                    drawing.current = false;
                  }}
                  onPointerLeave={() => {
                    drawing.current = false;
                  }}
                />
                <button type="button" onClick={clearSignature} className="mt-2 text-sm text-muted-foreground transition-colors hover:text-foreground">
                  Clear signature
                </button>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving} className="gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSignature className="h-4 w-4" />}
            Confirm signature
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
